import { Controller } from "@hotwired/stimulus";
import { whenConsentGranted } from "../lib/cookiebot_consent";

const POLL_MS = 100;
const GIVE_UP_MS = 10000;

/**
 * Painted-door email capture for mixed-recipe boxes (cvr_multirecipe_101526).
 * Sits on the Explore Other Plans modal and swaps its recipe picker for the
 * email form and confirmation, so every exit lands back on the picker. The
 * server records the submit, so it survives a visitor who never grants
 * analytics consent.
 */
export default class extends Controller {
	static targets = [
		"picker",
		"back",
		"form",
		"confirmation",
		"email",
		"error",
		"submit",
	];

	static values = {
		recipe: String,
		dogCount: Number,
		invalidMessage: String,
		failedMessage: String,
	};

	/**
	 * Events queue until statistics consent lands, however late, so a visitor
	 * who accepts cookies after opening the modal still counts as a view.
	 */
	connect() {
		this.visit = 0;
		this.queue = [];
		this.consentSubscription = whenConsentGranted("statistics", () => {
			this.consented = true;
			this.flush();
		});
	}

	disconnect() {
		this.consentSubscription?.();
		this.consentSubscription = null;
		window.clearTimeout(this.retryId);
		this.retryId = null;
	}

	/**
	 * The link sits inside the picker, so opening the modal is when it is seen.
	 */
	opened() {
		this.capture("multi_recipe_link_viewed");
	}

	start() {
		this.show(this.formTarget);
		this.capture("multi_recipe_clicked");
		this.emailTarget.focus();
	}

	/**
	 * Also runs when the modal closes, so a reopen starts on the picker; the
	 * email keeps whatever the visitor last typed.
	 */
	showPicker() {
		this.clearError();
		this.show(this.pickerTarget);
	}

	/**
	 * Every view change starts a new visit, which lets a pending submit tell
	 * whether the visitor has left the form it came from.
	 *
	 * @param {HTMLElement} view - the picker, form or confirmation
	 */
	show(view) {
		this.visit += 1;
		[this.pickerTarget, this.formTarget, this.confirmationTarget].forEach(
			(el) => el.classList.toggle("d-none", el !== view),
		);
		this.backTarget.classList.toggle("d-none", view === this.pickerTarget);
	}

	clearError() {
		this.emailTarget.classList.remove("is-invalid");
	}

	/**
	 * @param {SubmitEvent} event
	 */
	async submit(event) {
		event.preventDefault();
		this.emailTarget.value = this.emailTarget.value.trim();
		if (!this.emailTarget.checkValidity()) {
			this.showError(this.invalidMessageValue);
			return;
		}

		const visit = this.visit;
		this.submitTarget.disabled = true;
		try {
			const response = await fetch(event.target.action, {
				method: "POST",
				headers: { Accept: "application/json" },
				body: new FormData(event.target),
			});
			if (visit !== this.visit) return;
			if (response.ok) {
				this.show(this.confirmationTarget);
				return;
			}

			this.showError(
				response.status === 422
					? this.invalidMessageValue
					: this.failedMessageValue,
			);
		} catch {
			if (visit !== this.visit) return;
			this.showError(this.failedMessageValue);
		} finally {
			this.submitTarget.disabled = false;
		}
	}

	/**
	 * Only a 422 or the browser's own check means the address is bad; any other
	 * failure asks for a retry so a valid email is never blamed.
	 *
	 * @param {string} message
	 */
	showError(message) {
		this.errorTarget.textContent = message;
		this.emailTarget.classList.add("is-invalid");
	}

	/**
	 * @param {string} event - PostHog event name
	 */
	capture(event) {
		this.queue.push(event);
		if (this.consented) this.flush();
	}

	/**
	 * posthog_setup injects the stub on DOMContentLoaded, after this connects,
	 * so window.posthog can still be missing once consent fires. Poll briefly
	 * rather than drop the queue; one timer serves every queued event.
	 *
	 * @param {number} waited - milliseconds spent waiting so far
	 */
	flush(waited = 0) {
		window.clearTimeout(this.retryId);
		if (this.queue.length === 0) return;

		if (typeof window.posthog?.capture === "function") {
			this.queue.splice(0).forEach((event) =>
				window.posthog.capture(event, {
					recipe: this.recipeValue,
					dog_count: this.dogCountValue,
				}),
			);
			return;
		}

		if (waited >= GIVE_UP_MS) return;

		this.retryId = window.setTimeout(
			() => this.flush(waited + POLL_MS),
			POLL_MS,
		);
	}
}
